import { type CoreEventBusEvents } from '@databases/core-event-bus';
import { container } from '@databases/service-container';
import { captureException } from '@sentry/core';

import { type Recyclable } from './types.ts';

/**
 * Registers modules that recycle when bridge database identity changes
 * (contentId, Canvas reference, space). Calls setup(initialContentId) on all modules,
 * then subscribes to bridgeDatabaseIdentityChanged. On each event, disposes all modules
 * and calls setup(contentId from payload).
 *
 * @param modules - Recyclable modules to manage. Execution order is array order.
 * @param initialContentId - Content ID for the initial setup.
 * @returns A cleanup function that disposes all modules and unsubscribes from the event bus.
 *
 * Modules with `skipInitialSetup === true` skip the first `setup()` (registration only); identity-driven
 * `setupAll` calls still invoke `setup` for every module.
 *
 * ---
 *
 * ## Why use this instead of `useEffect` in React components
 *
 * **1. Centralized, ordered execution.**
 * All identity-change side effects fire from this single call site in bootstrap, in a guaranteed
 * sequence: every `dispose` runs before any `setup`. `useEffect`s scattered across the component
 * tree are scheduled independently by React — there are no ordering guarantees between them.
 *
 * **2. Non-UI code does not belong in the React tree.**
 * Closing a side panel or invalidating a cache has nothing to do with rendering. Placing it in a
 * component couples it to that component's mount/unmount lifecycle. If the component ever moves,
 * gets lazy-loaded, or unmounts early, the side effect silently stops firing.
 *
 * **3. Performance — no extra render cycles.**
 * A `useEffect` on `contentId` re-runs after every commit where `contentId` changed. Five such
 * effects at different tree levels means five separate post-commit scheduler passes. Recyclables
 * run synchronously in bootstrap — zero render cycles triggered, zero scheduler overhead.
 *
 * **4. Easier to test in isolation.**
 * A `Recyclable` is a plain object with `setup` and `dispose`. Tests call those methods directly
 * with no React renderer, no `act()`, and no component tree. Testing `useEffect` always requires
 * mounting a component.
 *
 * **5. Explicit lifecycle — no accidental double-fire.**
 * Each `useEffect` must defensively track "did I already run for this id?" with a `useRef`.
 * Recyclables receive a `dispose` call before the next `setup`, so the lifecycle is explicit by
 * design rather than guarded by refs.
 */
export function registerRecyclableModules(
  modules: Recyclable[],
  initialContentId: string,
): VoidFunction {
  let isFirstSetupPass = true;

  function setupAll(contentId: string) {
    for (const module of modules) {
      if (isFirstSetupPass && module.skipInitialSetup === true) {
        continue;
      }

      module.setup(contentId);
    }

    isFirstSetupPass = false;
  }

  function disposeAll() {
    for (const module of modules) {
      try {
        module.dispose();
      } catch (error) {
        captureException(error);
      }
    }
  }

  setupAll(initialContentId);

  const eventBus = container.resolve('EventBus');

  const onBridgeIdentityChanged = (
    data: CoreEventBusEvents['bridgeDatabaseIdentityChanged'],
  ) => {
    disposeAll();
    setupAll(data.contentId);
  };

  eventBus.on('bridgeDatabaseIdentityChanged', onBridgeIdentityChanged);

  return () => {
    disposeAll();
    eventBus.remove('bridgeDatabaseIdentityChanged', onBridgeIdentityChanged);
  };
}
